import React, { useEffect, useState } from "react";
import { apiGet, apiPost } from "../utils/api";
import { FaCheck, FaTag, FaRupeeSign } from "react-icons/fa";
import { Link } from "react-router-dom";
import { Spinner, PageHeader, EmptyState } from "../components/ui";
import { useModal } from "../context/ModalContext";

export default function Pricing() {
  const modal = useModal();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creatingSession, setCreatingSession] = useState(null);
  const [interval, setInterval] = useState("lifetime");
  const [currentSub, setCurrentSub] = useState(null);
  const [couponCode, setCouponCode] = useState("");
  const [showCoupon, setShowCoupon] = useState(false);
  // Auth is handled via httpOnly cookies; check role as a hint for logged-in state
  const isLoggedIn = !!localStorage.getItem("role");

  const fetchPlans = async () => {
    try {
      const [resPlans, resSub] = await Promise.all([
        apiGet("/api/plans"),
        isLoggedIn ? apiGet("/api/me/subscription") : Promise.resolve(null),
      ]);
      setPlans(resPlans?.plans || resPlans?.data?.plans || []);
      if (resSub?.success && resSub?.subscription) {
        setCurrentSub(resSub);
      }
    } catch (e) {
      console.error("Failed to load plans:", e);
      setPlans([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const handleCheckout = async (planId, planInterval) => {
    if (!isLoggedIn) return modal.alert("Please log in to upgrade a plan.", "warning");
    try {
      setCreatingSession(planId);
      const effectiveInterval = planInterval || interval;

      // If user has an active subscription and it's not a lifetime purchase, use upgrade endpoint
      if (currentSub?.subscription?.stripeSubscriptionId && effectiveInterval !== 'lifetime') {
        const res = await apiPost(`/api/upgrade/${planId}`, { interval: effectiveInterval });
        if (res?.success) {
          modal.alert("Plan updated successfully!", "success");
          // Refresh subscription data
          const refreshed = await apiGet("/api/me/subscription");
          if (refreshed?.success) setCurrentSub(refreshed);
        } else {
          modal.alert(res?.message || "Failed to upgrade plan.", "error");
        }
      } else {
        // New checkout
        const body = { interval: effectiveInterval };
        if (couponCode.trim()) body.couponCode = couponCode.trim();
        const res = await apiPost(`/api/checkout/${planId}`, body);
        if (res?.url) window.location.href = res.url;
        else modal.alert(res?.message || "Unable to start checkout session.", "error");
      }
    } catch (e) {
      console.error(e);
      modal.alert(e?.message || "Failed to process request.", "error");
    } finally {
      setCreatingSession(null);
    }
  };

  if (loading) {
    return (
      <div className="page-content flex items-center justify-center min-h-[400px]">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!plans.length) {
    return (
      <div className="page-content">
        <EmptyState title="No plans available" description="Check back later for available plans." />
      </div>
    );
  }

  const hasYearlyPricing = plans.some((p) => p.prices?.some((pr) => pr.interval === "year" && pr.amount > 0));
  const hasLifetimePricing = plans.some((p) => p.prices?.some((pr) => pr.interval === "lifetime" && pr.amount > 0));
  const currentPlanKey = currentSub?.subscription?.planKey;
  const isActive = currentSub?.subscription?.status === "active";

  const getButtonLabel = (plan) => {
    const isLifetime = plan.prices?.some(p => p.interval === "lifetime" && p.amount > 0);
    if (isLifetime) return "Get Deal";
    if (!isActive) return "Get Started";
    if (plan.key === currentPlanKey) return "Current Plan";

    // Determine if upgrade or downgrade based on price
    const currentPlan = plans.find(p => p.key === currentPlanKey);
    const currentMonthly = currentPlan?.prices?.find(p => p.interval === "month")?.amount || 0;
    const thisMonthly = plan.prices?.find(p => p.interval === "month")?.amount || 0;
    return thisMonthly > currentMonthly ? "Upgrade" : "Downgrade";
  };

  return (
    <div className="page-content">
      <div className="flex items-center justify-between mb-0">
        <PageHeader
          title="Choose Your Plan"
          subtitle="Select the perfect plan for your business needs"
        />
        <Link
          to="/pricing/inr"
          className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-surface-border text-sm font-medium text-ink-secondary hover:bg-surface-bg transition-colors"
        >
          <FaRupeeSign size={12} /> Pay in INR
        </Link>
      </div>

      {/* Billing Toggle: Lifetime first, then Yearly. Monthly is moved below the cards. */}
      {(hasYearlyPricing || hasLifetimePricing) && (
        <div className="flex items-center justify-center mb-8">
          <div className="inline-flex items-center rounded-lg border border-surface-border bg-surface-bg p-1 gap-1">
            {hasLifetimePricing && (
              <button
                type="button"
                onClick={() => setInterval("lifetime")}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                  interval === "lifetime"
                    ? "bg-brand text-white shadow-sm"
                    : "text-ink-muted hover:text-ink-primary"
                }`}
              >
                Lifetime
                <span className="ml-1.5 text-[10px] opacity-80">Best value</span>
              </button>
            )}
            {hasYearlyPricing && (
              <button
                type="button"
                onClick={() => setInterval("year")}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                  interval === "year"
                    ? "bg-brand text-white shadow-sm"
                    : "text-ink-muted hover:text-ink-primary"
                }`}
              >
                Yearly
                <span className="ml-1.5 text-[10px] opacity-80">Save more</span>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Coupon Code */}
      {!isActive && (
        <div className="flex justify-center mb-6">
          {!showCoupon ? (
            <button
              className="text-sm text-brand hover:text-brand-700 flex items-center gap-1.5"
              onClick={() => setShowCoupon(true)}
            >
              <FaTag size={12} /> Have a coupon code?
            </button>
          ) : (
            <div className="flex items-center gap-2 max-w-xs w-full">
              <input
                className="input flex-1"
                placeholder="Enter coupon code"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
              />
              <button
                className="btn-secondary btn-sm"
                onClick={() => { setShowCoupon(false); setCouponCode(""); }}
              >
                Remove
              </button>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6">
        {plans.filter((plan) => {
          if (plan.isFree) return false; // free plan deprecated; new signups get a 14-day Pro trial
          const hasMonthly = plan.prices?.some((p) => p.interval === "month" && p.amount > 0);
          const hasYearly = plan.prices?.some((p) => p.interval === "year" && p.amount > 0);
          const hasLifetime = plan.prices?.some((p) => p.interval === "lifetime" && p.amount > 0);
          if (interval === "lifetime") return hasLifetime;
          if (interval === "year") return hasYearly;
          return hasMonthly;
        }).map((plan) => {
          const isPopular = plan.badge;
          const isCurrent = plan.key === currentPlanKey && isActive;
          const monthlyPrice = plan.prices?.find((p) => p.interval === "month" && p.amount > 0);
          const yearlyPrice = plan.prices?.find((p) => p.interval === "year" && p.amount > 0);
          const lifetimePrice = plan.prices?.find((p) => p.interval === "lifetime" && p.amount > 0);

          let currentPrice;
          if (interval === "lifetime") {
            currentPrice = lifetimePrice;
          } else if (interval === "year") {
            currentPrice = yearlyPrice;
          } else {
            currentPrice = monthlyPrice;
          }

          const displayAmount = currentPrice?.amount || 0;
          const displayInterval = currentPrice?.interval || "month";
          // The interval sent to checkout must match the price we're displaying
          const checkoutInterval = currentPrice?.interval || interval;
          const buttonLabel = getButtonLabel(plan);

          return (
            <div
              key={plan.id}
              className={`card relative overflow-hidden transition-all ${
                isCurrent
                  ? "border-brand shadow-brand ring-1 ring-brand/20"
                  : isPopular
                  ? "border-brand/50 ring-1 ring-brand/20"
                  : ""
              }`}
            >
              {plan.badge && (
                <div className="absolute top-0 right-0">
                  <span className="badge-brand text-xs px-3 py-1 rounded-bl-lg rounded-tr-lg">
                    {plan.badge}
                  </span>
                </div>
              )}

              <div className="p-6">
                <h3 className="text-lg font-semibold text-ink-primary mb-1">{plan.name}</h3>
                <p className="text-sm text-ink-muted mb-4">{plan.description}</p>

                <div className="mb-6">
                  <span className="text-4xl font-extrabold tracking-tighter text-ink-primary">
                    ${displayAmount}
                  </span>
                  {displayInterval === "lifetime" ? (
                    <span className="text-ink-muted text-sm"> one-time</span>
                  ) : (
                    <span className="text-ink-muted text-sm">
                      /{displayInterval === "year" ? "year" : "month"}
                    </span>
                  )}
                  {interval === "year" && yearlyPrice?.amount > 0 && monthlyPrice?.amount > 0 && (
                    <p className="text-xs text-ink-faint mt-1">
                      ${(yearlyPrice.amount / 12).toFixed(2)}/mo billed annually
                    </p>
                  )}
                  {interval === "lifetime" && lifetimePrice?.amount > 0 && (
                    <p className="text-xs text-ink-faint mt-1">
                      Pay once, use forever
                    </p>
                  )}
                </div>

                <button
                  className={`${isPopular || isCurrent ? "btn-primary" : "btn-secondary"} w-full mb-6`}
                  onClick={() => handleCheckout(plan.id, checkoutInterval)}
                  disabled={creatingSession === plan.id || isCurrent}
                >
                  {creatingSession === plan.id ? (
                    <span className="flex items-center justify-center gap-2">
                      <Spinner size="sm" /> Processing...
                    </span>
                  ) : (
                    buttonLabel
                  )}
                </button>

                {plan.features?.length > 0 && (() => {
                  const moduleGroups = {};
                  plan.features.forEach((f) => {
                    const mod = f.module || "General";
                    if (!moduleGroups[mod]) moduleGroups[mod] = [];
                    moduleGroups[mod].push(f);
                  });
                  return (
                    <div className="space-y-4">
                      {Object.entries(moduleGroups).sort(([a], [b]) => a.localeCompare(b)).map(([mod, modFeatures]) => (
                        <div key={mod}>
                          <p className="text-xs font-semibold text-ink-muted uppercase tracking-wider mb-2">{mod}</p>
                          <div className="space-y-2">
                            {modFeatures.map((f, i) => (
                              <div key={i} className="flex items-start gap-2.5">
                                <FaCheck size={12} className="text-brand mt-0.5 flex-shrink-0" />
                                <span className="text-sm text-ink-secondary">
                                  {f.label || f.key}
                                  {typeof f.value === "boolean"
                                    ? null
                                    : <>: {f.value === -1 || f.value === "-1"
                                        ? "Unlimited"
                                        : Number(f.value)?.toLocaleString() || f.value}</>}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </div>
          );
        })}
      </div>

      {/* De-emphasized monthly switch. Anchors conversion to lifetime/yearly. */}
      <div className="flex justify-center pb-4">
        {interval !== "month" ? (
          <button
            type="button"
            onClick={() => setInterval("month")}
            className="text-sm text-ink-muted hover:text-ink-primary underline-offset-4 hover:underline transition-colors"
          >
            Prefer monthly billing? Switch to monthly →
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setInterval(hasLifetimePricing ? "lifetime" : "year")}
            className="text-sm text-ink-muted hover:text-ink-primary underline-offset-4 hover:underline transition-colors"
          >
            ← Save more with yearly or lifetime plans
          </button>
        )}
      </div>
    </div>
  );
}
